<template>
    <div class="reclamation" :class="{ active: isActive }">
        <div class="reclamation__header">
            <h5 class="reclamation__header-title" v-text="title" />

            <div class="reclamation__header-status">
                <span class="status" :class="status.class" v-text="status.text" />
            </div>

            <div class="reclamation__header-details">
                <div>
                    <strong>Data:</strong> {{ reclamationDate.format('DD-MM-YYYY') }}
                </div>
                <div>
                    <strong>Kwartał:</strong> {{ quarter }}
                </div>
                <div>
                    <strong>Nr reklamacji:</strong> {{ reclamation.id }}
                </div>
            </div>

            <div class="reclamation__header-action">
                <btn-details
                    class="reclamation__more"
                    :active="isActive"
                    @click="isActive = !isActive"
                />
            </div>
        </div>

        <transition-slide>
            <div v-if="isActive" class="reclamation__details">
                <div class="reclamation__details-row mt-4">
                    <strong v-text="'Sieć: '" />
                    <span v-text="reclamation.network" />
                </div>

                <div class="reclamation__details-row">
                    <strong v-text="'Sklep: '" />
                    <span v-text="merchant ? `${merchant.name}, ${merchant.fullAddress}` : '-'" />
                </div>

                <div class="reclamation__details-row">
                    <strong v-text="'SAP: '" />
                    <span v-text="reclamation.sapWholesaleShop || '-'" />
                </div>

                <div class="reclamation__details-row">
                    <strong v-text="'Kontrahent: '" />
                    <span v-text="reclamation.contractor" />
                </div>

                <div class="reclamation__details-row">
                    <strong v-text="'Reklamowana pozycja retra: '" />
                    <span v-text="reclamation.element" />
                </div>

                <div class="reclamation__details-row">
                    <strong>
                        Wartość obrotu:
                        <a-icon
                            v-tooltip="'Deklarowana wartość obrotu z dokumentów zakupowych u dostawcy'"
                            style="width: 18px; display: inline-block; transform: translate(2px, -1px);"
                            icon="Info"
                        />
                    </strong>
                    <span v-text="reclamation.value" />
                </div>

                <div class="reclamation__details-row">
                    <strong v-text="'Opis: '" />
                    <span v-text="reclamation.description" />
                </div>

                <div class="reclamation__details-row">
                    <strong v-text="'Status: '" />
                    <span v-text="status.text" />
                </div>

                <div class="reclamation__details-row">
                    <strong v-text="'Komentarz: '" />
                    <span v-text="message" />
                </div>

                <div class="reclamation__details-row">
                    <strong v-text="'Załączniki: '" />
                    <span v-if="!attachments.length" v-text="'-'" />
                </div>

                <promotion-slider
                    v-if="attachments.length"
                    class="reclamation__attachments mt-3"
                    :data="attachments"
                    :responsive="{
                        120: {
                            items: 1
                        },
                        400: {
                            items: 2
                        },
                        520: {
                            items: 3
                        },
                        620: {
                            items: 4
                        },
                        768: {
                            items: 3
                        },
                        900: {
                            items: 4
                        },
                        1100: {
                            items: 5
                        },
                        1420: {
                            items: 6
                        }
                    }"
                />
            </div>
        </transition-slide>
    </div>
</template>

<script>
export default {
    name: 'ReclamationPreview',

    data() {
        return {
            isActive: false
        }
    },

    props: {
        reclamation: {
            type: Object,
            required: true
        }
    },

    computed: {
        reclamationDate() {
            return this.$moment(this.reclamation.reclamationDate, 'DD-MM-YYYY')
        },

        quarter() {
            const { quarter, year } = this.reclamation

            if (quarter && year) {
                return `${quarter}-${year}`
            }

            return '-'
        },

        status() {
            switch (this.reclamation.status) {
            case 'Z':
                return {
                    class: 'is-success',
                    text: 'Zaakceptowana'
                }
            case 'X':
                return {
                    class: 'is-error',
                    text: 'Odrzucona'
                }
            default:
                return {
                    class: 'is-warning',
                    text: 'W toku'
                }
            }
        },

        title() {
            const { type, contractor, element } = this.reclamation
            return [type, contractor, element].join(' / ')
        },

        merchant() {
            const locationId = this.reclamation.locationId
            const merchants = this.$store.getters.getMerchants

            return merchants.find(merchant => merchant.location_id === locationId)
        },

        message() {
            const messages = (this.reclamation.messages || [])
            if (!messages.length) {
                return '-'
            }

            // Display last message
            return messages[messages.length - 1].message
        },

        attachments() {
            return (this.reclamation.attachments || []).map(el => {
                return {
                    name: el.fileName,
                    fileName: el.fileName,
                    urls: {
                        original: el.link
                    }
                }
            })
        }
    }
}
</script>

<style lang="scss">
@import '@/styles/_custom.scss';
@import '~bootstrap/scss/mixins';

.reclamation {
    --width-actions: 150px;
    --gap: 1rem;

    background-color: var(--color-white);
    border: 1px solid var(--color-gray-300);
    padding: 1rem;
    border-radius: var(--border-radius);
    color: var(--color-primary);

    .status {
        width: 100%;
    }

    + .reclamation {
        margin-top: 1rem;
    }

    &__header {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 1rem;
    }

    &__header-details {
        width: calc(100% - var(--width-actions) - var(--gap));
        display: flex;
        gap: 0.5rem;
        justify-content: space-between;

        > * {
            width: 33.3333%;
        }
    }

    &__header-title {
        width: calc(100% - var(--width-actions) - var(--gap));
        margin-bottom: 0;
    }

    &__header-action, &__header-status {
        width: var(--width-actions);
        display: flex;
        justify-content: flex-end;
    }

    &__more {
        margin-left: auto;
    }

    &:not(.active) {
        .reclamation__attachments {
            --slider-height: 0;
            --slider-hover-height: 0;
            .m-promotionSlider__slideName {
                font-size: 0 !important;
            }
        }
    }

    &__details-row {
        display: flex;
        gap: 1rem;
        margin-top: 1rem;

        > :nth-child(1) {
            width: 40%;
        }
        > :nth-child(2) {
            width: 60%;
            color: var(--text-gray);
        }
    }

    @include media-breakpoint-down(sm) {
        &__header-status {
            order: 3;
        }
        &__header-title {
            order: 1;
            width: 100%;
        }
        &__header-details {
            order: 2;
            width: 100%;
            flex-wrap: wrap;

            > * {
                flex: 1;
                width: auto;
            }
        }
        &__header-action {
            order: 4;
            margin-left: auto;
            width: auto;
        }
    }

    @include media-breakpoint-down(ms) {
        --width-actions: 147px;
        &__header-details {
            flex-direction: column;
            > * {
                width: 100%;
            }
        }
    }
}
</style>